import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Opens a time grid on now (14 Oct calls: the calendar must not open on 00:00, "scrolling to now, as dev already did").
 *
 * Puts the current time in the upper third of the grid's scroller. It fires once per (key | first hour | last hour) and
 * only when the scroller can actually scroll, so it waits for the real hours and bookings to land (the grids first render
 * a default 08:00–17:00 range or a skeleton) and never fights the user's own scrolling afterwards. A one-shot latch on
 * mount is what kept Week, Doctor and Room on 00:00 (15 Oct).
 */
export function useScrollToNow(
  ref: RefObject<HTMLElement | null>,
  { startHour, endHour, hourHeight, key = '' }: { startHour: number; endHour: number; hourHeight: number; key?: string },
) {
  const done = useRef('');
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const k = `${key}|${startHour}|${endHour}`;
    if (done.current === k) return;
    if (el.scrollHeight <= el.clientHeight) return;
    const now = new Date();
    const mins = Math.min(Math.max(now.getHours() * 60 + now.getMinutes(), startHour * 60), endHour * 60);
    el.scrollTop = Math.max(0, ((mins - startHour * 60) / 60) * hourHeight - el.clientHeight / 3);
    done.current = k;
  });
}
